import { Button, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { MooneIcon } from "Components/Shared/Icons/MooneIcon";
import {
  MULTI_PAYER_ACTION_SX,
  MultiPayerPaymentRequestPill,
} from "Components/Chat/MultiPayerPaymentRequestPill";
import type { MultiPayerRequestState } from "Components/Chat/multiPayerRequestState";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Chat.MultiPayerRequest;
const PAYMENT_MENU_RESOURCES = Resources.Chat.PaymentMenu;

// Reserves the outlined Close button's 1px border on the states that have none.
const INVISIBLE_BORDER = "1px solid transparent";

type Props = {
  /** The resolved state, or `restricted`: payable in principle, but not by this Lite viewer. */
  variant: MultiPayerRequestState["kind"] | "restricted";
  isClosing: boolean;
  onClose: () => void;
  onPay: () => void;
};

export const MultiPayerPaymentRequestAction = ({
  variant,
  isClosing,
  onClose,
  onPay,
}: Props) => {
  const { t } = useResource();
  const theme = useTheme();

  switch (variant) {
    case "closed":
      return (
        <MultiPayerPaymentRequestPill
          label={t(RESOURCES.Closed)}
          backgroundColor={grey[400]}
        />
      );
    case "closable":
      return (
        <Button
          variant="outlined"
          fullWidth
          disabled={isClosing}
          onClick={onClose}
          sx={MULTI_PAYER_ACTION_SX}
        >
          {t(RESOURCES.Close)}
        </Button>
      );
    case "payable":
      return (
        <Button
          variant="contained"
          fullWidth
          startIcon={<MooneIcon sx={{ color: "common.white", fontSize: 20 }} />}
          onClick={onPay}
          sx={{
            ...MULTI_PAYER_ACTION_SX,
            border: INVISIBLE_BORDER,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          {t(RESOURCES.Pay)}
        </Button>
      );
    case "unavailable":
      // A request whose attachment names someone other than its author (see
      // resolveMultiPayerRequestState): say so rather than leave the slot silently empty.
      return (
        <MultiPayerPaymentRequestPill
          label={t(RESOURCES.Unavailable)}
          backgroundColor={grey[400]}
        />
      );
    case "restricted":
      // A Lite viewer cannot pay a customer's request (App ADR-0013): the API would refuse it,
      // the card says why instead.
      return (
        <MultiPayerPaymentRequestPill
          label={t(PAYMENT_MENU_RESOURCES.LitePayUnavailable)}
          backgroundColor={grey[400]}
          fontSize={14}
        />
      );
  }
};
